import classNames from 'classnames';
import { useMemo, useState } from 'react';

import { Ratio } from '../../helpers/images';
import { AspectRatio } from '../AspectRatio/AspectRatio';
import { Image } from '../Image';
import { AuthenticatedPlayButton } from './AuthenticatedPlayButton';
import { VideoPlayButton } from './VideoPlayButton';

export interface InlineVideoProps {
  autoplay?: boolean;
  className?: string;
  cover: string;
  onPlay?: () => void;
  platform: 'vimeo' | 'youtube';
  playButton: boolean;
  playButtonLabel?: string;
  ratio?: Ratio;
  requiresAuthToPlay?: boolean;
  title: string;
  videoId: string;
}

export const InlineVideo = ({
  autoplay = false,
  className = '',
  cover,
  onPlay,
  platform,
  playButton = false,
  playButtonLabel,
  ratio = '16/9',
  requiresAuthToPlay = false,
  title,
  videoId,
}: InlineVideoProps) => {
  const [ready, setReady] = useState(false);

  const handlePlay = () => {
    onPlay?.();
    setReady(true);
  };

  const url = useMemo(() => {
    if (platform === 'vimeo') {
      let options = '';

      if (playButton) {
        options = '?autoplay=1';
      } else if (autoplay) {
        options = '?background=1&autoplay=1';
      }

      return `https://player.vimeo.com/video/${videoId}${options}`;
    }

    return `https://www.youtube.com/embed/${videoId}`;
  }, [autoplay, platform, playButton, videoId]);

  return (
    <AspectRatio
      className={classNames('group overflow-hidden rounded-sm', className)}
      sm={ratio}
    >
      {ready ? (
        <iframe
          allow="autoplay"
          allowFullScreen={false}
          className="h-full w-full"
          src={url}
          title={title}
        />
      ) : (
        <div className="absolute inset-0">
          <Image
            alt={title}
            hoverEffect
            onLoad={() => setReady(!playButton)}
            size={{
              sm: { ratio, span: 6 },
            }}
            src={cover}
          />

          <span className="absolute inset-0 bg-neutral-6/40 transition duration-300 ease-out group-hover:bg-neutral-6/0" />

          {playButton &&
            (requiresAuthToPlay ? (
              <AuthenticatedPlayButton
                label={playButtonLabel}
                onPlay={handlePlay}
                title={title}
                variant="light"
              />
            ) : (
              <VideoPlayButton
                label={playButtonLabel}
                onClick={handlePlay}
                variant="light"
              />
            ))}
        </div>
      )}
    </AspectRatio>
  );
};
